import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { requiredMessage, FieldError, Req, ERROR_COLOR } from '../../utils/formErrors';

export const LINK_TYPE_OPTIONS = [
  { value: 'youtube',   label: 'YouTube' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'link',      label: 'Lien externe' },
];

export function detectPlatform(url = '') {
  if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('facebook') || url.includes('fb.watch')) return 'facebook';
  if (url.includes('tiktok')) return 'tiktok';
  if (url.includes('instagram')) return 'instagram';
  return 'link';
}

/* ── Composant carte affiche avec 3 modes de media — partagé entre la
   config globale de la plateforme (LandingSettings, superadmin) et les
   paramètres d'une église (SiteSettings, admin d'église). ── */
export default function AfficheCard({ item, index, onChange, onDelete, s }) {
  const [mediaMode, setMediaMode] = useState(item.image_url ? 'url' : 'url');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);

  const isVideo = item.image_url && (
    item.image_url.endsWith('.mp4') || item.image_url.endsWith('.webm') ||
    item.image_url.endsWith('.mov') || item.image_url.includes('/media/landing/')
  );

  const handleUpload = async (file) => {
    if (!file) return;
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) { toast.error('Fichier trop lourd (max 50 Mo)'); return; }
    setUploading(true); setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/notifications/upload-media/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setUploadProgress(Math.round(e.loaded * 100 / e.total)),
      });
      onChange({ image_url: res.data.url });
      toast.success('Fichier uploadé !');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur upload');
    } finally { setUploading(false); setUploadProgress(0); }
  };

  const inp = { ...s.input };

  return (
    <div style={{ background: 'var(--page-bg)', borderRadius: 14, border: '1px solid var(--border)', padding: '18px', marginBottom: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, fontSize: '0.65rem', fontWeight: 800, color: '#7c3aed' }}>{index + 1}</div>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', flex: 1 }}>Affiche {index + 1}</span>
        <button onClick={onDelete} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.72rem', color: '#dc2626', fontWeight: 700 }}>Supprimer</button>
      </div>

      {/* Titre + Badge */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={s.label}>Titre<Req /></label>
          <input style={{ ...inp, ...(!(item.title || '').trim() ? { borderColor: ERROR_COLOR } : {}) }} value={item.title || ''} onChange={e => onChange({ title: e.target.value })} placeholder="Culte en direct ce dimanche"/>
          <FieldError message={!(item.title || '').trim() ? requiredMessage('Titre') : ''} />
        </div>
        <div>
          <label style={s.label}>Badge <span style={{ fontWeight:400, color:'#9ca3af' }}>(optionnel)</span></label>
          <input style={inp} value={item.badge || ''} onChange={e => onChange({ badge: e.target.value })} placeholder="LIVE · Nouveau · Annonce"/>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 14 }}>
        <label style={s.label}>Description <span style={{ fontWeight:400, color:'#9ca3af' }}>(optionnel)</span></label>
        <textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={item.desc || ''} onChange={e => onChange({ desc: e.target.value })} placeholder="Rejoignez-nous ce dimanche à 9h00..."/>
      </div>

      {/* ── Section média ── */}
      <div style={{ borderRadius: 12, border: '1.5px solid #e5e7eb', overflow: 'hidden', marginBottom: 14 }}>
        {/* Onglets mode */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          {[
            { id:'url',    label:'URL d\'image', icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> },
            { id:'upload', label:'Uploader',     icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> },
            { id:'video',  label:'Lien vidéo',   icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg> },
          ].map(tab => (
            <button key={tab.id} onClick={() => setMediaMode(tab.id)}
              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 8px', border:'none', cursor:'pointer', fontSize:'0.8rem', fontWeight:700, background:mediaMode===tab.id?'white':'transparent', color:mediaMode===tab.id?'#7c3aed':'#64748b', borderBottom:mediaMode===tab.id?'2px solid #7c3aed':'2px solid transparent', transition:'all .15s' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 14 }}>
          {/* Mode URL */}
          {mediaMode === 'url' && (
            <div>
              <label style={s.label}>URL directe de l'image</label>
              <input style={inp} value={item.image_url || ''} onChange={e => onChange({ image_url: e.target.value })} placeholder="https://exemple.com/affiche.jpg" type="url"/>
              {item.image_url && !isVideo && (
                <div style={{ marginTop: 10, borderRadius: 10, overflow:'hidden', maxHeight: 140, background:'#111' }}>
                  <img src={item.image_url} alt="preview" style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} onError={e=>e.target.style.display='none'}/>
                </div>
              )}
            </div>
          )}

          {/* Mode Upload */}
          {mediaMode === 'upload' && (
            <div>
              <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display:'none' }} onChange={e => handleUpload(e.target.files?.[0])}/>
              {item.image_url ? (
                <div style={{ position:'relative' }}>
                  {isVideo
                    ? <video src={item.image_url} controls style={{ width:'100%', maxHeight:160, borderRadius:10, background:'#111' }}/>
                    : <div style={{ borderRadius: 10, overflow:'hidden', maxHeight: 140, background:'#111' }}><img src={item.image_url} alt="preview" style={{ width:'100%', height:140, objectFit:'cover', display:'block' }}/></div>
                  }
                  <button onClick={() => { onChange({ image_url:'' }); fileRef.current.value=''; }}
                    style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.6)', border:'none', borderRadius:8, padding:'4px 10px', color:'white', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
                    Changer
                  </button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  style={{ width:'100%', padding:'28px 20px', border:'2px dashed #d1d5db', borderRadius:12, background:'transparent', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:10, transition:'all .15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='#7c3aed';e.currentTarget.style.background='#faf5ff';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#d1d5db';e.currentTarget.style.background='transparent';}}>
                  {uploading ? (
                    <>
                      <div style={{ width:'100%', maxWidth:200, height:6, background:'#e5e7eb', borderRadius:999 }}>
                        <div style={{ height:'100%', width:`${uploadProgress}%`, background:'#7c3aed', borderRadius:999, transition:'width .2s' }}/>
                      </div>
                      <span style={{ fontSize:'0.82rem', color:'#7c3aed', fontWeight:600 }}>Upload {uploadProgress}%…</span>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#374151' }}>Cliquer pour uploader</div>
                        <div style={{ fontSize:'0.75rem', color:'#9ca3af', marginTop:3 }}>Image (JPG, PNG, WebP) ou Vidéo (MP4, MOV) — max 50 Mo</div>
                      </div>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Mode Lien vidéo */}
          {mediaMode === 'video' && (
            <div>
              <label style={s.label}>URL de la vidéo (YouTube, Facebook, TikTok...)</label>
              <input style={inp} value={item.link_url || ''} onChange={e => onChange({ link_url: e.target.value, link_type: detectPlatform(e.target.value) })} placeholder="https://youtu.be/... ou https://fb.watch/...  " type="url"/>
              {item.link_url && (
                <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:6, fontSize:'0.78rem', color:'#64748b' }}>
                  Plateforme détectée :
                  <span style={{ fontWeight:700, color:'#7c3aed', textTransform:'capitalize' }}>{detectPlatform(item.link_url)}</span>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <label style={s.label}>Image d'aperçu <span style={{ fontWeight:400, color:'#9ca3af' }}>(optionnel — thumbnail)</span></label>
                <input style={inp} value={item.image_url || ''} onChange={e => onChange({ image_url: e.target.value })} placeholder="https://... (thumbnail de la vidéo)" type="url"/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lien (si pas mode vidéo) */}
      {mediaMode !== 'video' && (
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10 }}>
          <div>
            <label style={s.label}>Plateforme</label>
            <select style={{ ...inp, cursor:'pointer' }} value={item.link_type || 'link'} onChange={e => onChange({ link_type: e.target.value })}>
              {LINK_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>URL du lien <span style={{ fontWeight:400, color:'#9ca3af' }}>(clic sur l'affiche)</span></label>
            <input style={inp} value={item.link_url || ''} onChange={e => onChange({ link_url: e.target.value })} placeholder="https://..." type="url"/>
          </div>
        </div>
      )}

      {/* Liens supplémentaires nommés (Facebook, Instagram...) — affichés comme
          boutons distincts dans la modal détail, en plus du lien principal ci-dessus. */}
      <div style={{ marginTop: 14 }}>
        <label style={s.label}>Liens supplémentaires <span style={{ fontWeight:400, color:'#9ca3af' }}>(optionnel — Facebook, Instagram...)</span></label>
        {(item.links || []).map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, marginBottom: 8 }}>
            <input style={inp} value={l.label} onChange={e => onChange({ links: item.links.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} placeholder="Instagram"/>
            <input style={inp} value={l.url} onChange={e => onChange({ links: item.links.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })} placeholder="https://..."/>
            <button type="button" onClick={() => onChange({ links: item.links.filter((_, j) => j !== i) })}
              style={{ background:'#f1f5f9', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:6, fontWeight:600, cursor:'pointer', padding:'0 12px', fontSize:'0.8rem' }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={() => onChange({ links: [...(item.links || []), { label: '', url: '' }] })}
          style={{ background:'#f1f5f9', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:6, fontWeight:600, cursor:'pointer', padding:'7px 12px', fontSize:'0.78rem' }}>
          + Ajouter un lien
        </button>
      </div>
    </div>
  );
}
